import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchGraphDataCached } from './GraphPlaceholder';
import { authFetch, isInteractiveAuthError } from '../authFetch';

// Slow fallback endpoint (same one used inside GraphPlaceholder if fast path fails per column fetch)
const READ_ULOG_API_ENDPOINT =
  process.env.REACT_APP_READ_ULOG_API_ENDPOINT ||
  'https://hjv8khvwpd.execute-api.eu-west-2.amazonaws.com/readUlogDataForGraph-dev';

// GPS validation helper function
const isValidGPS = (lat, lon) => {
  return Number.isFinite(lat) && Number.isFinite(lon) && 
         lat >= -90 && lat <= 90 && 
         lon >= -180 && lon <= 180 &&
         !(Math.abs(lat) < 0.001 && Math.abs(lon) < 0.001);
};

// Manual Leaflet map implementation to avoid react-leaflet double init under React 19 reappear effects.

// We'll use a lightweight circle marker instead of the default large pin.
const createVehicleMarker = (lat, lng) => {
  return L.circleMarker([lat, lng], {
    radius: 4,
    color: '#000',
    weight: 1,
    fillColor: '#fff',
    fillOpacity: 0.9
  });
};

/**
 * FlightMap renders a Leaflet map showing the flight path derived from latitude/longitude columns.
 * It also displays a moving marker synced to currentTime (seconds) using the shared time array.
 */
export default function FlightMap({ flightUuid, currentTime, onDomain, isLive = false }) {
  const [pathPoints, setPathPoints] = useState([]); // Full raw [{lat,lng,time}] (no trimming so playback can animate entire timeline)
  const mapRef = useRef(null); // L.Map instance
  const containerRef = useRef(null); // DOM node
  const polylineRef = useRef(null);
  const traveledRef = useRef(null);
  const markerRef = useRef(null);
  const hasFitRef = useRef(false);
  const lastDomainRef = useRef({ xMax: null });
  // Sustained movement window metadata (domain suggestion only). Kept for potential future UI (e.g., highlighting active movement segment).
  const movementWindowRef = useRef({ startTime: null, endTime: null });
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  // Fetch flight data for lat/lon columns
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!flightUuid) {
        setPathPoints([]); setStatus({ state: 'idle', message: '' }); return;
      }
      
      // Use different column names for live vs historical flights
      const cols = isLive 
        ? ['latitude', 'longitude']  // Live flights use simplified column names
        : ['vehicle_global_position.lat', 'vehicle_global_position.lon'];  // Historical flights use ULog names
        
      setStatus({ state: 'loading', message: 'Loading GPS path…' });
      try {
        // First attempt: cached fast fetch (which internally may already try fast + fallback for combined columns)
        let data; let fastFailed = false;
        try {
          data = await fetchGraphDataCached(flightUuid, cols, { live: isLive, liveStrict: isLive, allowLiveFallback: true });
          if (isLive) {
            // try { console.debug('[MapLiveFetch] live path used', { flightUuid, cols, samples: data?.times?.length }); } catch(_) {}
          }
        } catch (fastErr) {
          fastFailed = true;
          console.warn('Fast path failed for map, attempting slow fallback', fastErr);
        }

        if (!data || !data.times) {
          if (isLive) {
            // For live, do not fallback to historical endpoints; treat as waiting/no data
            setStatus({ state: 'empty', message: 'Waiting for live GPS…' });
            setPathPoints([]);
            return;
          }
          // Explicit slow fallback: POST to read ULOG endpoint
            try {
              const resp = await authFetch(READ_ULOG_API_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ flight_uuid: flightUuid, columns: cols })
              });
              if (!resp.ok) throw new Error('Slow fallback HTTP ' + resp.status);
              data = await resp.json();
            } catch (slowErr) {
              if (cancelled) return;
              if (isInteractiveAuthError(slowErr)) return;
              const msg = fastFailed ? 'Unable to retrieve GPS data (fast + fallback failed).' : 'Unable to retrieve GPS data.';
              setStatus({ state: 'error', message: msg });
              setPathPoints([]); return;
            }
        }

        if (cancelled) return;
        const { times = [], data: series = {}, launch_offset_s = null } = data;
        
        // Use appropriate column names based on live vs historical
        const latKey = isLive ? 'latitude' : 'vehicle_global_position.lat';
        const lonKey = isLive ? 'longitude' : 'vehicle_global_position.lon';
        const lats = series[latKey] || [];
        const lons = series[lonKey] || [];
        const raw = [];
        const len = Math.min(times.length, lats.length, lons.length);
        let filteredCount = 0;
        for (let i = 0; i < len; i++) {
            const lat = Number(lats[i]);
            const lon = Number(lons[i]);
            const t = Number(times[i]);
            
            // Use GPS validation to filter out invalid coordinates
            if (Number.isFinite(t) && isValidGPS(lat, lon)) {
              raw.push({ lat, lng: lon, time: t });
            } else {
              filteredCount++;
            }
        }
        
        if (filteredCount > 0) {
          // console.log(`[MapLoad] Filtered out ${filteredCount} invalid GPS points out of ${len} total`);
        }
        if (raw.length) {
          // --- Sustained movement window detection ---
          // We still detect a representative movement window for domain reporting, but we now
          // KEEP the entire raw path untrimmed so that the marker can animate smoothly even
          // before/after sustained movement (e.g. taxiing, brief hover, or short dead time).
          const distMeters = (a,b) => {
            const dLat = (b.lat - a.lat) * Math.PI/180;
            const dLon = (b.lng - a.lng) * Math.PI/180;
            const r = 6371000; // meters (earth approx)
            const sa = Math.sin(dLat/2);
            const sb = Math.sin(dLon/2);
            const h = sa*sa + Math.cos(a.lat*Math.PI/180)*Math.cos(b.lat*Math.PI/180)*sb*sb;
            return 2*r*Math.asin(Math.min(1, Math.sqrt(h)));
          };

          // Heuristics:
          // windowSeconds: min duration of sustained movement window we require
          // minDistanceInWindow: total distance traveled inside that window
            const windowSeconds = 2.0;
            const minDistanceInWindow = 3.0; // meters across the window
            const pointCount = raw.length;
          // Pre-compute prefix sums of distance and times for O(1) window distance queries.
          const timesArr = raw.map(p => p.time);
          const distPrefix = new Array(pointCount).fill(0);
          for (let i=1;i<pointCount;i++) {
            distPrefix[i] = distPrefix[i-1] + distMeters(raw[i-1], raw[i]);
          }
          const timeAt = idx => timesArr[idx];
          // Find first index where any window starting there meets both criteria.
          let moveStartIdx = 0;
          for (let i=0;i<pointCount;i++) {
            // Binary search for window end time >= timeAt(i)+windowSeconds
            const target = timeAt(i) + windowSeconds;
            let lo2 = i, hi2 = pointCount - 1, endIdx = i;
            while (lo2 <= hi2) {
              const mid = (lo2 + hi2) >> 1;
              if (timeAt(mid) >= target) { endIdx = mid; hi2 = mid - 1; } else { lo2 = mid + 1; }
            }
            if (timeAt(endIdx) - timeAt(i) < windowSeconds) continue; // insufficient duration
            const distanceInWindow = distPrefix[endIdx] - distPrefix[i];
            if (distanceInWindow >= minDistanceInWindow) { moveStartIdx = i; break; }
          }
          // Find movement end symmetrically from tail
          let moveEndIdx = pointCount - 1;
          for (let i=pointCount-1;i>=0;i--) {
            const target = timeAt(i) - windowSeconds;
            let lo2 = 0, hi2 = i, startIdx = i;
            while (lo2 <= hi2) {
              const mid = (lo2 + hi2) >> 1;
              if (timeAt(mid) <= target) { startIdx = mid; lo2 = mid + 1; } else { hi2 = mid - 1; }
            }
            if (timeAt(i) - timeAt(startIdx) < windowSeconds) continue;
            const distanceInWindow = distPrefix[i] - distPrefix[startIdx];
            if (distanceInWindow >= minDistanceInWindow) { moveEndIdx = i; break; }
          }
          if (moveEndIdx < moveStartIdx) { moveStartIdx = 0; moveEndIdx = pointCount - 1; }
          // Full, untrimmed path used for rendering & animation
          setPathPoints(raw);

          // Determine domain (movement window) while preserving early launch offset if earlier.
          const movementStartTime = raw[moveStartIdx]?.time ?? (launch_offset_s || null);
          const movementEndTime = raw[moveEndIdx]?.time ?? movementStartTime;
          movementWindowRef.current = { startTime: movementStartTime, endTime: movementEndTime };
          if (typeof onDomain === 'function' && raw.length) {
            const domainStart = (launch_offset_s != null && Number.isFinite(launch_offset_s) && launch_offset_s < movementStartTime)
              ? launch_offset_s
              : movementStartTime;
            const domainEnd = movementEndTime;
            try { onDomain({ xMin: domainStart, xMax: domainEnd, launchOffset: launch_offset_s }); } catch(e) {}
          }
          setStatus({ state: 'ready', message: '' });
        } else {
          setPathPoints([]);
          const missingCols = (!lats.length && !lons.length);
          setStatus({ state: 'empty', message: missingCols ? 'GPS columns missing or empty.' : 'No valid GPS samples.' });
        }
      } catch (outer) {
        if (cancelled) return;
        console.error('Map path fetch outer error', outer);
        setStatus({ state: 'error', message: 'Unexpected error loading GPS data.' });
        setPathPoints([]);
      }
    };
    load();
    return () => { cancelled = true; };
  // onDomain intentionally excluded to avoid refiring domain detection if parent provides unstable callback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightUuid, isLive]);

  // Live polling: refresh path every 1.5s if live (slower than graphs to avoid interference)
  useEffect(() => {
    if (!isLive || !flightUuid) return;
    // console.log('[MapPolling] ENABLED for live flight:', flightUuid);
    let cancelled = false;
    
    // Start with 300ms delay to stagger with graph polling
    const timeout = setTimeout(() => {
      if (cancelled) return;
      
      const interval = setInterval(async () => {
        if (cancelled) return;
        // console.log('[MapPolling] Polling tick for:', flightUuid);
        try {
          // Use live column names for polling
          const cols = ['latitude', 'longitude'];
          const data = await fetchGraphDataCached(flightUuid, cols, { live: true, liveStrict: true, allowLiveFallback: true });
          if (!data || !Array.isArray(data.times)) return;
          const { times = [], data: series = {}, launch_offset_s = null } = data;
          const lats = series['latitude'] || [];  // Use live column name
          const lons = series['longitude'] || [];  // Use live column name
          const len = Math.min(times.length, lats.length, lons.length);
          if (len === 0) return;
          // Build new raw array
          const raw = [];
          let filteredCount = 0;
          for (let i=0;i<len;i++) {
            const lat = Number(lats[i]);
            const lon = Number(lons[i]);
            const t = Number(times[i]);
            
            // Use GPS validation to filter out invalid coordinates
            if (Number.isFinite(t) && isValidGPS(lat, lon)) {
              raw.push({ lat, lng: lon, time: t });
            } else {
              filteredCount++;
            }
          }
          
          if (filteredCount > 0) {
            // console.log(`[MapPolling] Filtered out ${filteredCount} invalid GPS points out of ${len} total`);
          }
          if (!raw.length) return;
          
          setPathPoints(prev => {
            // More detailed change detection - compare actual data, not just length
            if (prev.length === raw.length) {
              const lastPrev = prev[prev.length - 1];
              const lastRaw = raw[raw.length - 1];
              if (lastPrev && lastRaw && 
                  Math.abs(lastPrev.lat - lastRaw.lat) < 0.0001 && 
                  Math.abs(lastPrev.lng - lastRaw.lng) < 0.0001 &&
                  Math.abs(lastPrev.time - lastRaw.time) < 0.001) {
                return prev; // no meaningful change
              }
            }
            // console.log('[MapPolling] Updating path with', raw.length, 'points');
            return raw;
          });
          // Update domain only if extended
          const newXMax = raw[raw.length - 1].time;
          if (typeof onDomain === 'function' && (lastDomainRef.current.xMax == null || newXMax > lastDomainRef.current.xMax)) {
            lastDomainRef.current.xMax = newXMax;
            try { onDomain({ xMin: raw[0].time, xMax: newXMax, launchOffset: launch_offset_s }); } catch(_) {}
          }
        } catch (e) {
          console.warn('[MapPolling] Error:', e);
          // Swallow intermittent live polling errors
        }
      }, 1500); // Slower map polling (1.5s) to avoid interfering with graph updates
      
      // Store interval reference for cleanup
      if (!cancelled) {
        return () => { clearInterval(interval); };
      }
    }, 300); // 300ms stagger delay
    
    return () => { 
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [isLive, flightUuid, onDomain]);

  // Determine marker position for current time (nearest sample <= currentTime)
  const markerPos = useMemo(() => {
    if (!pathPoints.length) return null;
    let lo = 0, hi = pathPoints.length - 1, idx = 0;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (pathPoints[mid].time <= currentTime) { idx = mid; lo = mid + 1; } else { hi = mid - 1; }
    }
    return pathPoints[idx];
  }, [pathPoints, currentTime]);

  // Initialize map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    mapRef.current = L.map(containerRef.current, {
      center: [0,0],
      zoom: 2,
      preferCanvas: true,
      zoomControl: true,
      attributionControl: true
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(mapRef.current);
    setTimeout(()=> { try { mapRef.current.invalidateSize(); } catch(e) {} }, 100);
  }, []);

  // Update polyline + bounds when pathPoints change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Remove existing polyline if any
    if (polylineRef.current) {
      try { map.removeLayer(polylineRef.current); } catch(e) {}
      polylineRef.current = null;
    }
    if (traveledRef.current) {
      try { map.removeLayer(traveledRef.current); } catch(e) {}
      traveledRef.current = null;
    }
    if (pathPoints.length > 1) {
      const latLngs = pathPoints.map(p => [p.lat, p.lng]);
      // Base full path (very faint dashed context)
      polylineRef.current = L.polyline(latLngs, { color: '#000000', weight: 3, opacity: 0.15, dashArray: '4,8' });
      polylineRef.current.addTo(map);
      // Traveled path (up to current time) - initial build (maybe empty until markerPos computed)
      const traveledLatLngs = [];
      traveledRef.current = L.polyline(traveledLatLngs, { color: '#000000', weight: 3, opacity: 0.9 });
      traveledRef.current.addTo(map);
      if (latLngs.length && !hasFitRef.current) {
        try { map.fitBounds(L.latLngBounds(latLngs), { padding: [12,12] }); hasFitRef.current = true; } catch(e) {}
      }
    }
  }, [pathPoints]);

  // Update traveled path when currentTime / markerPos changes
  useEffect(() => {
    if (!traveledRef.current || !pathPoints.length) return;
    // Binary search for last point <= currentTime
    let lo = 0, hi = pathPoints.length - 1, best = 0;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (pathPoints[mid].time <= currentTime) { best = mid; lo = mid + 1; } else { hi = mid - 1; }
    }
    const traveledLatLngs = pathPoints.slice(0, best + 1).map(p => [p.lat, p.lng]);
    traveledRef.current.setLatLngs(traveledLatLngs);
  }, [currentTime, pathPoints]);

  // Update marker position
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!markerPos) {
      if (markerRef.current) { try { map.removeLayer(markerRef.current); } catch(e) {} markerRef.current = null; }
      return;
    }
    if (!markerRef.current) {
      markerRef.current = createVehicleMarker(markerPos.lat, markerPos.lng);
      markerRef.current.addTo(map);
    } else if (markerRef.current.setLatLng) {
      markerRef.current.setLatLng([markerPos.lat, markerPos.lng]);
    }
  }, [markerPos]);

  // Cleanup on unmount
  useEffect(() => () => {
    try { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } } catch(e) {}
  }, []);

  // Always mount a single MapContainer after a flight is chosen to avoid Leaflet double init.
  const showOverlay = (!flightUuid) || status.state !== 'ready' || !pathPoints.length;
  const overlayMessage = (() => {
    if (!flightUuid) return 'Select a flight to load map.';
    if (status.state === 'ready' && pathPoints.length) return '';
    if (status.state === 'loading') return status.message || 'Loading GPS path…';
    if (status.state === 'empty') return status.message || 'No GPS data.';
    if (status.state === 'error') return status.message || 'Error loading GPS data.';
    return 'Loading…';
  })();

  return (
    <div className="flight-map-wrapper">
      <div ref={containerRef} className="flight-map" />
      {showOverlay && (
        <div className={`map-status-overlay status-${!flightUuid ? 'idle' : status.state}`}>
          {overlayMessage}
        </div>
      )}
    </div>
  );
}
